import { css } from '@emotion/react'
import { makeUseSessionScrollRestore } from '@mathflat/design-system/@common/hooks/makeSessionScrollRestore.tsx'
import { isDevelopMode } from '@mathflat/shared/@common/utils/dev'
import { pipe } from 'effect'
import * as O from 'effect/Option'
import { observer, useLocalObservable } from 'mobx-react'
import { useLayoutEffect, useState } from 'react'

import { useCallbackOnVisibilityChange } from '~/@common/hooks/useCallbackOnVisibilityChange'
import { useStudentAppMediaQuery } from '~/@common/hooks/useMediaQuery'
import { useRepository } from '~/@common/hooks/useRepository.ts'
import { errorHandlerService } from '~/@common/services'
import { commonRepo } from '~/@common/services/repo.service.ts'
import { colors } from '~/@common/styles'
import { mediaQuery } from '~/@common/styles/mediaQuery'
import { FloatingLoader } from '~/@common/ui/(Loader)/FloatingLoader.tsx'
import { scrollToTarget } from '~/@common/utils/domUtils'
import type { WorkbookSearchModalOnSubmit } from '~/@pages/@widgets/WorkbookSearchModal'
import { WorkbookSearchService } from '~/@pages/@widgets/WorkbookSearchModal/WorkbookSearch.service'
import {
  ChallengeStore,
  DEFAULT_WORKBOOK_SEARCH_TAB_TYPE,
} from '~/@pages/student/@common/store/Challenge.store.ts'
import SmileChallenge from '~/@pages/student/@widgets/SmileChallenge/SmileChallenge.tsx'
import { ChallengeService } from '~/@pages/student/challenge/@widget/Challenge.service.ts'
import ChallengeStartDrawer from '~/@pages/student/challenge/@widget/ChallengeStartDrawer/ChallengeStartDrawer.tsx'

import { ConceptualLearningService } from '../conceptual-learning/@widgets/service/ConceptualLearning.service'
import AssignedChallengeContents from './@widget/AssignedChallengeContents/AssignedChallengeContents'
import ChallengeContents from './@widget/ChallengeContents/ChallengeContents'
import ChallengeFilters from './@widget/ChallengeFilters/ConceptChipFilters'
import type { ChallengeTabType } from './@widget/ChallengeTabContainer/ChallengeTabContainer'
import ChallengeTabContainer from './@widget/ChallengeTabContainer/ChallengeTabContainer'

export const kind = 'ms__ChallengePage' as const

const scrollValueSessionKey = `${kind}/scrollValue`
const useStoreRestore = makeUseSessionScrollRestore(scrollValueSessionKey)

const ChallengePage = () => {
  useCallbackOnVisibilityChange()
  const challengeStore = useRepository(ChallengeStore)
  const challengeService = useRepository(ChallengeService)
  const searchService = useRepository(WorkbookSearchService)
  const conceptualLearningService = useRepository(ConceptualLearningService)

  const { isMobile } = useStudentAppMediaQuery()
  const isAssignedChallengeTabEnabled =
    commonRepo.permission.byFeature.isAllowed('CHALLENGE_SETTING')

  const [isReady, setIsReady] = useState(false)
  const [selectedMiddleChapterId, setSelectedMiddleChapterId] = useState<number | null>(null)
  const [selectedChallengeTab, setSelectedChallengeTab] = useState<ChallengeTabType>(
    isAssignedChallengeTabEnabled ? 'ASSIGNED_CHALLENGE' : 'CURRICULUM_CHALLENGE',
  )

  const { scrollRefCallback, innerRefCallback, innerStyle, setScroll } = useStoreRestore<
    HTMLElement,
    HTMLDivElement
  >()

  const localStore = useLocalObservable(() => ({
    scrollReset() {
      setScroll(0)
    },
    get middleChapterIds() {
      return pipe(
        challengeService.getFilteredIdMapFromKindTag('middleChapterId'),
        O.flatMap((ids) => {
          if (ids.size === 0) return O.none()
          return O.some([...ids.keys()])
        }),
      )
    },

    async onSearch(
      payload: Parameters<WorkbookSearchModalOnSubmit>[0],
    ): ReturnType<WorkbookSearchModalOnSubmit> {
      if (!commonRepo.studentId) {
        throw Error('studentId를 알 수 없습니다.')
      }

      const searchData = { ...payload }
      const isCurriculumChanged = payload.curriculumKey !== challengeStore.curriculumKey

      if (isCurriculumChanged) {
        searchData.workbookList = []
        searchData.selectedWorkbookType = DEFAULT_WORKBOOK_SEARCH_TAB_TYPE

        const storedWorkbooksByCurriculumKey =
          challengeStore.workbooksByCurriculumKey[payload.curriculumKey]

        if (storedWorkbooksByCurriculumKey) {
          searchData.workbookList = storedWorkbooksByCurriculumKey.workbooks
          searchData.selectedWorkbookType = storedWorkbooksByCurriculumKey.workbookType
        }
      }

      await challengeService.fetch({
        studentId: commonRepo.studentId,
        curriculumKey: searchData.curriculumKey,
        workbookIds: searchData.workbookList.map((workbook) => workbook.id),
      })

      challengeStore.setCurriculumWorkbooks({
        curriculumKey: searchData.curriculumKey,
        workbookType: searchData.selectedWorkbookType,
        workbooks: searchData.workbookList,
        searchText: searchData.searchText,
      })
      challengeStore.saveToChallengeRemoteStorage()
      searchService.init({
        selectedWorkbooks: searchData.workbookList,
        curriculumKey: searchData.curriculumKey,
        searchText: searchData.searchText,
        selectedWorkbookType: searchData.selectedWorkbookType,
      })
      searchService.unchanged()
    },

    async loadChallenges(studentId: string) {
      try {
        await challengeStore.loadFromChallengeRemoteStorage()

        if (challengeStore.curriculumKey) {
          searchService.init({
            selectedWorkbooks: challengeStore.workbooks,
            curriculumKey: challengeStore.curriculumKey,
            searchText: challengeStore.searchText ?? '교재・교과서 선택',
            selectedWorkbookType: challengeStore.selectedWorkbookType,
          })

          await challengeService.fetch({
            studentId: studentId,
            curriculumKey: challengeStore.curriculumKey,
            workbookIds: challengeStore.workbooks.map((workbook) => workbook.id),
          })
          await conceptualLearningService.loadConceptualWorksheets()
        }
        setIsReady(true)
      } catch (err) {
        errorHandlerService.handle(err)
      }
    },
    onSelectMiddleChapter(middleChapterId: number) {
      setSelectedMiddleChapterId(middleChapterId)
      scrollIntoMiddleChapter(middleChapterId)
    },
  }))

  const scrollIntoMiddleChapter = (middleChapterId: number) => {
    const targetElement = document.querySelector<HTMLDetailsElement>(
      `[data-middle-chapter-id="${middleChapterId}"]`,
    )
    if (!targetElement) return

    const scrollContainer = isMobile
      ? (document.querySelector('.container') as HTMLElement | null)
      : (targetElement.closest('.simplebar-content-wrapper') as HTMLElement | null)

    if (scrollContainer) {
      scrollToTarget(scrollContainer, targetElement)
    } else {
      // 컨테이너를 못 찾으면 fallback
      targetElement.scrollIntoView({ behavior: 'smooth' })
    }
  }

  const resetWorkbookFilterOptions = () => {
    const curriculumKey = challengeStore.curriculumKey

    if (!curriculumKey) {
      throw new Error('curriculumKey가 없습니다.')
    }

    const payload = {
      curriculumKey,
      selectedWorkbookType: DEFAULT_WORKBOOK_SEARCH_TAB_TYPE,
      workbookList: [],
      searchText: '',
    }
    localStore.onSearch(payload)
    searchService.clear()
    searchService.unchanged()
  }

  const handleSearchWithScrollReset = async (
    payload: Parameters<WorkbookSearchModalOnSubmit>[0],
  ) => {
    await localStore.onSearch(payload)
    if (!selectedMiddleChapterId) {
      localStore.scrollReset()
    }
  }
  const handleSelectChallengeTab = (tab: ChallengeTabType) => {
    challengeStore.clearSelectedChallenge()
    setSelectedChallengeTab(tab)
  }

  useLayoutEffect(() => {
    if (!commonRepo.studentId) {
      return
    }

    localStore.loadChallenges(commonRepo.studentId).then(() => {
      if (isDevelopMode) {
        globalThis.challengeService = challengeService
        globalThis.challengeStore = challengeStore
      }
    })
  }, [localStore, challengeService, challengeStore])

  const desktopChallengeContent = (
    <div className="container">
      <section className="challenge-main">
        <ChallengeFilters
          onSearch={handleSearchWithScrollReset}
          onWorkbookReset={resetWorkbookFilterOptions}
          onSelectMiddleChapter={localStore.onSelectMiddleChapter}
          selectedMiddleChapterId={selectedMiddleChapterId}
          resetSelectedMiddleChapterId={() => setSelectedMiddleChapterId(null)}
        />
        <ChallengeContents
          isEmpty={challengeService.isEmpty}
          middleChapterIds={localStore.middleChapterIds}
          selectedMiddleChapterId={selectedMiddleChapterId}
          innerStyle={innerStyle}
          innerRef={innerRefCallback}
          scrollableRef={scrollRefCallback}
        />
        <ChallengeStartDrawer />
      </section>
    </div>
  )

  return (
    <div css={_css} data-component={kind}>
      {!isReady && <FloatingLoader bg={colors.white} />}
      {isMobile ? (
        // ===== Mobile/Tablet =====
        <>
          <ChallengeFilters
            onSearch={handleSearchWithScrollReset}
            onSelectMiddleChapter={localStore.onSelectMiddleChapter}
            selectedMiddleChapterId={selectedMiddleChapterId}
            resetSelectedMiddleChapterId={() => setSelectedMiddleChapterId(null)}
          />
          <div className="container" ref={scrollRefCallback}>
            <section className="challenge-main">
              <ChallengeContents
                isEmpty={challengeService.isEmpty}
                middleChapterIds={localStore.middleChapterIds}
                selectedMiddleChapterId={selectedMiddleChapterId}
                innerStyle={innerStyle}
                innerRef={innerRefCallback}
              />
              <ChallengeStartDrawer />
            </section>
            {challengeStore.curriculumKey && (
              <SmileChallenge curriculumKey={challengeStore.curriculumKey} />
            )}
          </div>
        </>
      ) : // ===== Desktop =====
      commonRepo.isElementarySchoolflat && isAssignedChallengeTabEnabled ? (
        <ChallengeTabContainer
          selectedTab={selectedChallengeTab}
          onSelectTab={handleSelectChallengeTab}
        >
          {selectedChallengeTab === 'ASSIGNED_CHALLENGE' ? (
            <AssignedChallengeContents />
          ) : (
            desktopChallengeContent
          )}
        </ChallengeTabContainer>
      ) : (
        <>
          <div className="desktop-content">{desktopChallengeContent}</div>
          {challengeStore.curriculumKey && (
            <SmileChallenge curriculumKey={challengeStore.curriculumKey} />
          )}
        </>
      )}
    </div>
  )
}

export default observer(ChallengePage)

const _css = css`
  position: relative;
  display: flex;
  height: 100%;
  overflow: hidden;

  .container {
    width: 100%;
    display: flex;
    gap: 24px;
    z-index: 1; // iOS Safari 스크롤바 이슈
  }

  .challenge-main {
    position: relative;
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    overflow: hidden;
  }

  .desktop-content {
    flex: 1;
    min-height: 0;
    overflow: hidden;
    padding: 20px 24px;
  }

  .desktop-content > .container {
    height: 100%;
  }

  ${mediaQuery.underTablet} {
    display: flex;
    flex-direction: column;
    padding: 20px 16px 0;

    .container {
      overflow: auto;
      display: flex;
      flex-direction: column;
      gap: 20px;
      padding-bottom: 40px;
      height: 100%;
    }

    .challenge-main {
      order: 1;
      height: auto;
      flex-grow: 1;
      overflow: unset;
    }
  }
`
